import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { BlockType } from '@common/entities';
import { RootState } from 'state/store';

import { mergeCommonKeys } from 'utils/utils';

import { getPageWiseFields } from '../utils/steps';

export const useGetPageData = () => {
  const { getValues } = useFormContext();

  const { form } = useSelector((state: RootState) => state.application);
  const { pages } = form;

  const pageWiseFields = React.useMemo(() => {
    return pages.map(({ sections, type }: any) => {
      return getPageWiseFields(sections, type);
    });
  }, [pages]);

  /**
   * Get single fields form value with name
   */
  function getSingleFieldValue(fields: string[]) {
    if (!fields?.length) return [];

    const applicantData: { [key: string]: any } = {};

    fields.forEach((field) => {
      const value = getValues(field);
      if (value !== undefined) {
        applicantData[field] = value;
      }
    });

    // The "I don't have a middle name" checkbox is a UI-only field on the
    // Personal Info page. It must be persisted alongside applicant_name_middle
    // so its checked state survives navigation/refresh (DEV-274944).
    if (fields.includes('applicant_name_middle')) {
      applicantData.dont_have_middle_name = !!getValues('dont_have_middle_name');
    }

    if (fields.includes('applicant_name_maternal_last')) {
      applicantData.dont_have_maternal_last_name = !!getValues('dont_have_maternal_last_name');
    }

    return [{ applicantData }];
  }

  const removeBlankObject = (data: { [k: string]: any }[]) => {
    return data?.filter((i) => {
      const [, value] = Object.entries(i)[0];

      if (!value) return false;
      return true;
    });
  };

  /**
   * Get block field form values
   * @param fields
   * @returns
   */
  function getBlockFieldsValue(fields: string[]) {
    if (!fields?.length) return [];

    const returnData: { [key: string]: any }[] = [];

    fields.forEach((field) => {
      switch (field) {
        case BlockType.Income:
          returnData.push({
            [BlockType.Income]: getValues(BlockType.Income),
          });
          break;
        case BlockType.Asset:
          returnData.push({
            [BlockType.Asset]: getValues(BlockType.Asset),
          });
          break;
        case BlockType.Pet:
          returnData.push({ [BlockType.Pet]: getValues(BlockType.Pet) });
          break;
        case BlockType.Coapplicant:
          returnData.push({
            coApplicant: getValues('coApplicant'),
          });
          break;
        case BlockType.Vehicle:
          returnData.push({
            [BlockType.Vehicle]: getValues(BlockType.Vehicle),
          });
          break;
        case BlockType.IdentificationType:
          returnData.push({
            [BlockType.IdentificationType]: getValues(BlockType.IdentificationType),
          });
          break;
        case BlockType.Guarantor:
          returnData.push({
            guarantor: getValues('guarantor'),
          });
          break;
        case BlockType.AddressCurrent:
          returnData.push({
            [BlockType.AddressCurrent]: getValues(BlockType.AddressCurrent),
          });
          break;
        case BlockType.AddressPrevious:
          returnData.push({
            [BlockType.AddressPrevious]: getValues(BlockType.AddressPrevious),
          });
          break;
        case BlockType.GeneralQuestion:
          returnData.push({
            [BlockType.GeneralQuestion]: getValues(BlockType.GeneralQuestion),
          });
          break;
        case BlockType.EmergencyContact:
          returnData.push({
            [BlockType.EmergencyContact]: getValues(BlockType.EmergencyContact),
          });
          break;
        case BlockType.Disability:
          returnData.push({
            [BlockType.Disability]: getValues(BlockType.Disability),
          });
          break;
        case BlockType.CitizenStatus:
          returnData.push({
            [BlockType.CitizenStatus]: getValues(BlockType.CitizenStatus),
          });
          break;
        case BlockType.Education:
          returnData.push({
            [BlockType.Education]: getValues(BlockType.Education),
          });
          break;
        case BlockType.Student:
          returnData.push({
            [BlockType.Student]: getValues(BlockType.Student),
          });
          break;
        case BlockType.HousedDeclaredDisaster:
          returnData.push({
            [BlockType.HousedDeclaredDisaster]: getValues(BlockType.HousedDeclaredDisaster),
          });
          break;
        case BlockType.HouseholdFleeingDomesticViolence:
          returnData.push({
            [BlockType.HouseholdFleeingDomesticViolence]: getValues(
              BlockType.HouseholdFleeingDomesticViolence,
            ),
          });
          break;
        case BlockType.MilitaryVeteran:
          returnData.push({
            [BlockType.MilitaryVeteran]: getValues(BlockType.MilitaryVeteran),
          });
          break;
        case BlockType.PoliceSecurityOfficer:
          returnData.push({
            [BlockType.PoliceSecurityOfficer]: getValues(BlockType.PoliceSecurityOfficer),
          });
          break;
        case BlockType.AnticipatedAdditionAdoption:
          returnData.push({
            [BlockType.AnticipatedAdditionAdoption]: getValues(
              BlockType.AnticipatedAdditionAdoption,
            ),
          });
          break;
        case BlockType.AnticipatedAdditionFosterChild:
          returnData.push({
            [BlockType.AnticipatedAdditionFosterChild]: getValues(
              BlockType.AnticipatedAdditionFosterChild,
            ),
          });
          break;
        case BlockType.AnticipatedAdditionPregnancy:
          returnData.push({
            [BlockType.AnticipatedAdditionPregnancy]: getValues(
              BlockType.AnticipatedAdditionPregnancy,
            ),
          });
          break;
        case BlockType.Expense:
          returnData.push({
            [BlockType.Expense]: getValues(BlockType.Expense),
          });
          break;
        case BlockType.RoommateInterests:
          returnData.push({
            [BlockType.RoommateInterests]: getValues(BlockType.RoommateInterests),
          });
          break;
        case BlockType.RoommateInvitations:
          {
            const roommateBlock = getValues(BlockType.RoommateInvitations) as any;
            const persistedBlock = roommateBlock ? { ...roommateBlock } : roommateBlock;
            if (persistedBlock && typeof persistedBlock === 'object') {
              // The draft roommate is submitted to Student separately. Persist only completed
              // roommate rows in the Form Builder response, including the entered email.
              delete persistedBlock.roommate;
            }
            returnData.push({ [BlockType.RoommateInvitations]: persistedBlock });
          }
          break;
        case BlockType.DepositAlternative:
          returnData.push({
            [BlockType.DepositAlternative]: getValues(BlockType.DepositAlternative),
          });
          break;
        case BlockType.DepositAlternative3rdParty:
          returnData.push({
            [BlockType.DepositAlternative3rdParty]: getValues(BlockType.DepositAlternative3rdParty),
          });
          break;
        case BlockType.InstallmentPlans:
          returnData.push({
            [BlockType.InstallmentPlans]: getValues(BlockType.InstallmentPlans),
          });
          break;
        case BlockType.PersonalInformation:
          returnData.push(
            getSingleFieldValue([
              'applicant_name_first',
              'applicant_name_last',
              'applicant_email_address',
              'applicant_birth_date',
              'applicant_primary_number',
            ])[0],
          );
          break;
        case BlockType.AlternateName:
          returnData.push(
            getSingleFieldValue([
              'alternate_first_name',
              'alternate_middle_name',
              'alternate_last_name',
            ])[0],
          );
          break;
        case BlockType.IDVerification:
          returnData.push({
            [BlockType.IDVerification]: getValues(BlockType.IDVerification),
          });
          break;
        case BlockType.IncomeVerification:
          returnData.push({
            [BlockType.IncomeVerification]: getValues(BlockType.IncomeVerification),
          });
          break;
        default:
          break;
      }
    });

    return removeBlankObject(returnData);
  }

  /**
   * Prepare the data for sending it to backend
   * Merges objects with common keys from singleField and blockField
   * @returns
   */
  const prepareData = (stepIndex: number) => {
    const singleField = getSingleFieldValue(pageWiseFields[stepIndex].singleFields);

    const blockField = getBlockFieldsValue(pageWiseFields[stepIndex].blockFields);

    return mergeCommonKeys(singleField, blockField);
  };

  return prepareData;
};
